import { baseApi } from "@/services/base-api";
import type {
  ChangePasswordInput,
  NotificationSettings,
  OwnProfile,
  PublicProfile,
  UpdateProfileInput,
} from "@/features/profile/types/profile";
import type { Product } from "@/features/marketplace/types/product";

/**
 * All under `/users`, matching docs/api/api-design.md's resource-based
 * convention — `/users/me` for the caller's own record (already existed),
 * `/users/:id` new for viewing anyone else's. `getSellerListings` reuses
 * the public `GET /products` endpoint with a `seller` filter rather than a
 * dedicated route — it's the same "active listings" query the marketplace
 * already serves, just scoped to one seller.
 */
export const profileApi = {
  getMyProfile: () => baseApi.get<OwnProfile>("/users/me"),

  updateMyProfile: (input: UpdateProfileInput) => baseApi.patch<OwnProfile>("/users/me", input),

  /** `Content-Type: null` strips the JSON default for this one multipart
   * request — see listings.api.ts's `createListing` for why a manually set
   * `multipart/form-data` value (missing its boundary) would break it. */
  uploadAvatar: (file: File) => {
    const formData = new FormData();
    formData.append("avatar", file);
    return baseApi.post<OwnProfile, FormData>("/users/me/avatar", formData, {
      headers: { "Content-Type": null },
    });
  },

  getPublicProfile: (userId: string) => baseApi.get<PublicProfile>(`/users/${userId}`),

  getSellerListings: (sellerId: string) =>
    baseApi.getPaginated<Product>("/products", { params: { seller: sellerId, limit: 24 } }),

  updateNotificationSettings: (input: NotificationSettings) =>
    baseApi.patch<OwnProfile>("/users/me/notification-settings", input),

  changePassword: (input: ChangePasswordInput) =>
    baseApi.patch<void>("/users/me/password", input),

  deleteAccount: () => baseApi.delete<void>("/users/me"),
};
